import { cn } from '../../lib/utils'

interface TabOption<T extends string> {
  value: T
  label: string
}

interface TabsProps<T extends string> {
  options: readonly TabOption<T>[]
  value: T
  onValueChange: (value: T) => void
  'aria-label'?: string
  className?: string
}

/**
 * Underline tabs sitting on a rule. Distinct from <Segmented>, which is a pill
 * group for switching a value inside a view; these switch the view itself.
 *
 * The rule is only as wide as the tabs. A full-bleed one reads as a section
 * divider and competes with the card below it, and it also means the strip can
 * sit on a row beside the actions for the view it is switching.
 */
function Tabs<T extends string>({
  options,
  value,
  onValueChange,
  className,
  ...props
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={props['aria-label']}
      className={cn('border-border inline-flex w-fit items-center gap-4 border-b', className)}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onValueChange(option.value)}
            className={cn(
              '-mb-px border-b-2 px-0.5 py-2 text-base font-medium transition-colors duration-100 ease-out outline-none',
              'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-1',
              active ? 'border-fg text-fg' : 'text-fg-muted hover:text-fg border-transparent',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export { Tabs, type TabOption, type TabsProps }
